import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import { bindOnce } from '../utils/bindOnce';

gsap.registerPlugin(ScrollTrigger);

// ============================================
// 設定値
// 挙動を変えたいときはここだけ書き換える
// ============================================
const CONFIG = {
  // --- Lenis スムーススクロール ---
  lenis_duration: 1.4,
  lenis_smoothWheel: true,

  // タッチ操作を Lenis に同期するか（false = OS ネイティブのタッチスクロールを使用）
  lenis_syncTouch: false,

  // ホイール 1 ノッチあたりのスクロール量の倍率
  lenis_wheelMultiplier: 0.9,

  // --- ページ内アンカーリンク ---
  anchor_duration: 1.2,
  anchor_lerp: 0.2,
  anchor_offset: 40,

  // --- スクロールアニメーション (.js-inview) ---
  // 'top 75%' = 要素の上端が画面の 75% ラインを越えたとき発火
  inview_start: 'top 75%',
};

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ============================================
// Lenis 初期化
// ============================================
export const lenis = new Lenis({
  duration: CONFIG.lenis_duration,
  smoothWheel: CONFIG.lenis_smoothWheel && !prefersReducedMotion,
  syncTouch: CONFIG.lenis_syncTouch,
  wheelMultiplier: CONFIG.lenis_wheelMultiplier,
});

lenis.on('scroll', ScrollTrigger.update);

const _raf = (time) => lenis.raf(time * 1000);
gsap.ticker.add(_raf);

gsap.ticker.lagSmoothing(0);
window.addEventListener('pageshow', (e) => {
  if (e.persisted) ScrollTrigger.refresh();
});

// ============================================
// ページ内アンカーリンクへのスムーススクロール
//
// 除外クラス:
//   .-no-smooth             : スムーススクロールを無効にしたいリンク
//   .l-header__skip-content : アクセシビリティ用スキップリンク
// ============================================
function lenisPageScroll() {
  const links = document.querySelectorAll('a[href^="#"]:not(.-no-smooth, .l-header__skip-content)');
  if (links.length === 0) return;

  const getHeaderH = () => Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--size-header-h')) || 0;
  const getAnchorOffset = () => {
    return -getHeaderH() - CONFIG.anchor_offset;
  };

  const scrollToAnchor = (target) => {
    if (prefersReducedMotion) {
      const el = target === 'body' ? document.body : document.querySelector(target);
      el?.scrollIntoView();
      return;
    }
    lenis.scrollTo(target, {
      offset: getAnchorOffset(),
      lerp: CONFIG.anchor_lerp,
      duration: CONFIG.anchor_duration,
    });
  };

  links.forEach((el) => {
    if (!bindOnce(el, 'smoothScroll')) return;

    el.addEventListener('click', (e) => {
      const targetSelector = el.getAttribute('href');

      if (!targetSelector || targetSelector === '#') {
        e.preventDefault();
        scrollToAnchor('body');
        return;
      }

      try {
        if (!document.querySelector(targetSelector)) return;
      } catch {
        return;
      }

      e.preventDefault();
      scrollToAnchor(targetSelector);
    });
  });
}

// ============================================
// スクロール検知アニメーション
//
// .js-inview クラスを持つ要素が画面内に入ると is-active クラスを付与する。
// CSS 側でトランジションを定義しておく。
// ============================================
function inViewAnm() {
  if (prefersReducedMotion) {
    document.querySelectorAll('.js-inview').forEach((el) => el.classList.add('is-active'));
    return;
  }

  ScrollTrigger.batch('.js-inview', {
    start: CONFIG.inview_start,
    once: true,
    onEnter: (batch) => {
      batch.forEach((el) => el.classList.add('is-active'));
    },
  });
}

// ============================================
// MVアニメーション
// ============================================
function mvAnim() {
  const mv = document.querySelector('.js-mv');

  if (mv) {
    const mm = gsap.matchMedia();
    mm.add('(max-width: 1023px)', () => {
      createMvTimeline(200, 0.85, 40, 'top 10%', 'top -380px');
    });
    function createMvTimeline(maxWidth, widthRatio, infoAccessTop) {
      gsap.set('.js-mv__video', {
        top: '50%',
        left: '50%',
        xPercent: -50,
        yPercent: -50,
        y: 0,
      });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: '.js-hero',
          start: 'top top',
          end: '+=180%',
          scrub: 1.2,
          pin: true,
          invalidateOnRefresh: true,
          // markers: true,
        },
      });
      tl.to(
        '.js-mv__txtArea',
        {
          y: -200,
          opacity: 0,
          ease: 'power2.out',
        },
        0
      );

      tl.to(
        '.js-mv__info',
        {
          xPercent: -50,
          y: () => -(window.innerHeight + document.querySelector('.js-mv__info > :last-child').offsetTop - infoAccessTop),
          ease: 'none',
          duration: 1.15,
        },
        0
      );

      tl.to(
        '.js-mv__video',
        {
          top: '50%',
          left: '50%',
          xPercent: -50,
          yPercent: -50,
          transformOrigin: 'center center',
          width: () => Math.min(maxWidth, window.innerWidth * widthRatio),
          height: () => {
            const targetWidth = Math.min(maxWidth, window.innerWidth * widthRatio);
            return targetWidth * (540 / 540);
          },
          ease: 'power2.out',
          duration: 0.35,
        },
        1.15
      );

      tl.to(
        '.js-mv__overlay',
        {
          opacity: 1,
          ease: 'none',
        },
        0.3
      );

      tl.to(
        '.js-mv__info',
        {
          opacity: 0,
          ease: 'power1.in',
          duration: 0.15,
        },
        1
      );

      tl.to(
        '.js-mv__video-txt',
        {
          opacity: 1,
          ease: 'power2.out',
        },
        '+<3'
      );
    }
    mm.add('(min-width: 1024px)', () => {
      gsap.set('.js-mv__video', {
        top: '50%',
        left: '50%',
        xPercent: -50,
        yPercent: -50,
        y: 0,
      });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: '.js-hero',
          start: 'top top',
          end: '+=150%',
          scrub: 1.2,
          pin: true,
          invalidateOnRefresh: true,
          // markers: true,
        },
      });

      tl.to(
        '.js-mv__txtArea',
        {
          y: -150,
          opacity: 0,
          ease: 'power2.inOut',
        },
        '<+=.2'
      );

      tl.to(
        '.js-mv__info',
        {
          y: -window.innerHeight * 0.9,
          duration: 2,
          ease: 'power1.inOut',
        },
        '<+=.3'
      );

      tl.to(
        '.js-mv__video',
        {
          top: '50%',
          left: '50%',
          xPercent: -50,
          yPercent: -50,
          y: 0,
          transformOrigin: 'center center',
          width: () => Math.min(540, window.innerWidth - 40),
          height: () => Math.min(540, window.innerHeight - 40),
          ease: 'power2.inOut',
        },
        '>'
      );

      tl.to(
        '.js-mv__overlay',
        {
          opacity: 1,
          ease: 'none',
        },
        '<'
      );

      gsap.to('.js-mv__info', {
        scrollTrigger: {
          trigger: '.js-mv__info',
          start: 'center 50%',
          end: 'top center',
          scrub: 1.8,
        },
        opacity: 0,
        ease: 'none',
      });

      tl.to(
        '.js-mv__video-txt',
        {
          opacity: 1,
          ease: 'power2.out',
        },
        '+<3'
      );
    });
  }
}

// ============================================
// ギャラリーのスクロールアニメーション
// ============================================
function slideLoop() {
  const target = document.querySelector('.js-scroll-slide');
  if (!target) return;

  window.addEventListener('scroll', () => {
    const rect = target.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    const progress = (windowHeight - rect.top) / (windowHeight + rect.height);

    if (progress >= 0 && progress <= 1) {
      const moveX = progress * 500; // 最大100px
      target.style.transform = `translateX(-${moveX}px)`;
    }
  });
}

// ============================================
// 2カラムのナビ
// ============================================
function sideNav() {
  const nav = document.querySelector('.js-nav');
  if (!nav) return;

  const trigger = nav.querySelectorAll('.js-nav__trigger');
  const sec = document.querySelectorAll('.js-nav__area');
  const accordions = nav.querySelectorAll('.c-side-nav__accordion');
  const sections = Array.from(sec);
  const triggerSections = new Map();
  let autoOpenEnabled = false;
  let refreshTimer = null;
  let pendingAccordion = null;
  let pendingOpenTimer = null;

  trigger.forEach((trigger_item) => {
    const href = trigger_item.getAttribute('href');
    if (!href) return;

    try {
      const target = document.querySelector(href);
      const section = target?.closest('.js-nav__area') || target;
      if (section) triggerSections.set(trigger_item, section);
    } catch {
      // 無効なセレクターは対象外
    }

    ScrollTrigger.create({
      trigger: trigger_item,
      start: () => 'top 75%',
      end: () => 'bottom 75%',
      endTrigger: '.js-nav__area',
    });
  });

  const refreshAfterAccordionChange = () => {
    if (refreshTimer) window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => {
      refreshTimer = null;
      ScrollTrigger.refresh();
    }, 900);
  };

  const isSp = () => window.matchMedia('(max-width: 1023px)').matches;

  const openAccordion = (accordion) => {
    const summary = accordion.querySelector('.js-accordion__ttlWrap');
    if (!summary || accordion.hasAttribute('open') || accordion.dataset.animStatus === 'running') return false;

    summary.click();
    return true;
  };

  const syncAccordionForSection = (section) => {
    if (isSp()) return;

    const trigger_item = Array.from(trigger).find((item) => triggerSections.get(item) === section);
    const accordion = trigger_item?.closest('.c-side-nav__accordion');
    const summary = accordion?.querySelector('.js-accordion__ttlWrap');
    if (!accordion || !summary) return;

    const openAccordions = Array.from(accordions).filter((item) => item !== accordion && item.hasAttribute('open'));
    openAccordions.forEach((item) => {
      item.querySelector('.js-accordion__ttlWrap')?.click();
    });

    if (pendingOpenTimer) window.clearTimeout(pendingOpenTimer);

    if (openAccordions.length > 0 || accordion.dataset.animStatus === 'running') {
      pendingAccordion = accordion;
      pendingOpenTimer = window.setTimeout(() => {
        pendingOpenTimer = null;
        if (pendingAccordion !== accordion || accordion.hasAttribute('open')) return;
        summary.click();
        refreshAfterAccordionChange();
      }, 850);
      refreshAfterAccordionChange();
      return;
    }

    if (openAccordion(accordion)) refreshAfterAccordionChange();
  };

  const openAccordionAtCurrentPosition = () => {
    const hash = window.location.hash;
    if (hash) {
      try {
        const target = document.querySelector(hash);
        const section = target?.closest('.js-nav__area') || target;
        if (sections.includes(section)) {
          addClassNav(section);
          syncAccordionForSection(section);
          return;
        }
      } catch {
        // 無効なハッシュはスクロール位置で判定
      }
    }

    if (isSp() || window.scrollY <= 0) return;

    const marker = window.innerHeight * 0.5;
    const currentSection = sections.find((target) => {
      const rect = target.getBoundingClientRect();
      return rect.top <= marker && rect.bottom >= marker;
    });
    if (currentSection) {
      addClassNav(currentSection);
      syncAccordionForSection(currentSection);
    }
  };

  //アクティブにする
  gsap.utils.toArray(sec).forEach((target) => {
    gsap.to(target, {
      scrollTrigger: {
        trigger: target,
        start: () => 'top 50%',
        end: () => 'bottom 50%',
        onEnter: () => {
          addClassNav(target);
          if (autoOpenEnabled) syncAccordionForSection(target);
        },
        onEnterBack: () => {
          addClassNav(target);
          if (autoOpenEnabled) syncAccordionForSection(target);
        },
      },
    });
  });

  const syncCurrentPosition = (enableAutoOpen = false) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        openAccordionAtCurrentPosition();
        if (enableAutoOpen) autoOpenEnabled = true;
      });
    });
  };

  // 初期化時とpageshow後の両方で、復元されたスクロール位置やハッシュに対応する
  syncCurrentPosition();
  window.addEventListener('pageshow', () => syncCurrentPosition(true));

  function addClassNav(target) {
    const idName = target.dataset.navTarget || target.id;
    trigger.forEach((item) => {
      const href = item.getAttribute('href');
      if (!href) return;

      const trigger_id = href.replace(/^#/, '');
      if (trigger_id === idName) {
        item.classList.add('-current');
      } else {
        if (item.classList.contains('-current')) {
          item.classList.remove('-current');
        }
      }
    });
  }
}

export { lenisPageScroll, inViewAnm, mvAnim, slideLoop, sideNav };
